/**
 * @praesto/core/rbac
 *
 * Client-side RBAC: a permission matrix built from the union of every loaded
 * module's `manifest.permissions` plus the current user's memberships.
 *
 * This is UX-level only (show/hide, enable/disable). Real enforcement lives
 * in Firestore rules + Cloud Functions. Decisions here are FAIL-CLOSED:
 * no matching permission definition ⇒ denied.
 *
 * Contract: packages/contracts/src/core-api.ts (RBACService).
 */
import type {
  Action,
  PermissionDefinition,
  RBACService,
  ResourceScope,
  ResourceType,
} from "@praesto/contracts";
import type { GlobalRole, HotelId, Membership, TenantRole } from "@praesto/shared-types";

export interface RbacOptions {
  readonly memberships: ReadonlyArray<Membership>;
  readonly permissions?: ReadonlyArray<PermissionDefinition>;
  readonly globalRole?: GlobalRole;
}

/** RBACService plus late-update hooks the host wires after sign-in. */
export interface RbacService extends RBACService {
  registerPermissions(defs: ReadonlyArray<PermissionDefinition>): void;
  setMemberships(memberships: ReadonlyArray<Membership>): void;
  setGlobalRole(role: GlobalRole | undefined): void;
}

export class PermissionDeniedError extends Error {
  constructor(action: string, resource: string) {
    super(`Permission denied: cannot "${action}" on "${resource}"`);
    this.name = "PermissionDeniedError";
  }
}

// superadmin is Mademind support: read-only access across every tenant,
// PLUS the host-level actions explicitly registered with `defaultRoles:
// ['superadmin']` (e.g. `manage:tenant`, `impersonate:tenant_owner`).
const READ_ONLY_ACTIONS: ReadonlySet<string> = new Set(["read", "audit", "export", "query"]);

/**
 * Permissions that belong to the host (apps/web), not to any module
 * package: managing tenant users and the hotel/department tree. Wired
 * into the RBAC matrix at boot via `registerModulePermissions(rbac, CORE_PERMISSIONS)`.
 */
export const CORE_PERMISSIONS: ReadonlyArray<PermissionDefinition> = [
  {
    action: "manage",
    resource: "user",
    description: { it: "Gestire utenti", en: "Manage users" },
    defaultRoles: ["tenant_owner", "manager_hotel"],
  },
  {
    action: "manage",
    resource: "hotel",
    description: { it: "Gestire hotel + dipartimenti", en: "Manage hotels + departments" },
    defaultRoles: ["tenant_owner"],
  },
  {
    action: "manage",
    resource: "tenant",
    description: { it: "Amministrare tenant", en: "Manage tenants" },
    defaultRoles: ["superadmin"],
  },
  {
    action: "impersonate",
    resource: "tenant_owner",
    description: { it: "Impersonare tenant owner per supporto", en: "Impersonate tenant owner" },
    defaultRoles: ["superadmin"],
  },
];

export function createRbacService(opts: RbacOptions): RbacService {
  let memberships = opts.memberships;
  let globalRole = opts.globalRole;
  const registry = new Map<string, PermissionDefinition>();
  const keyOf = (action: string, resource: string): string => `${action}::${resource}`;

  const registerPermissions = (defs: ReadonlyArray<PermissionDefinition>): void => {
    for (const def of defs) registry.set(keyOf(def.action, def.resource), def);
  };
  if (opts.permissions) registerPermissions(opts.permissions);

  const roleAt = (hotelId: HotelId): TenantRole | null => {
    const m = memberships.find((x) => x.hotelId === hotelId);
    return m ? m.role : null;
  };

  const effectiveRoles = (scope?: ResourceScope): ReadonlySet<TenantRole | GlobalRole> => {
    const roles = new Set<TenantRole | GlobalRole>();
    if (scope?.hotelId) {
      const r = roleAt(scope.hotelId);
      if (r) roles.add(r);
    } else {
      for (const m of memberships) roles.add(m.role);
    }
    if (globalRole === "tenant_owner") roles.add("tenant_owner");
    if (globalRole === "superadmin") roles.add("superadmin");
    return roles;
  };

  const can = (action: Action, resource: ResourceType, scope?: ResourceScope): boolean => {
    const def = registry.get(keyOf(action, resource));
    if (def && globalRole === "superadmin" && def.defaultRoles.includes("superadmin")) {
      return true;
    }
    if (globalRole === "superadmin") return READ_ONLY_ACTIONS.has(action);
    if (!def) return false; // fail-closed
    const roles = effectiveRoles(scope);
    return def.defaultRoles.some((r) => roles.has(r));
  };

  const assertCan = (action: Action, resource: ResourceType, scope?: ResourceScope): void => {
    if (!can(action, resource, scope)) {
      throw new PermissionDeniedError(action, resource);
    }
  };

  const setMemberships = (m: ReadonlyArray<Membership>): void => {
    memberships = m;
  };
  const setGlobalRole = (g: GlobalRole | undefined): void => {
    globalRole = g;
  };

  return {
    can,
    assertCan,
    roleAt,
    registerPermissions,
    setMemberships,
    setGlobalRole,
  };
}

/**
 * Registers a module's declared permissions into the live RBAC matrix.
 * Called by the host once a module's manifest is loaded. `CoreAPI.rbac` is
 * typed as `RBACService` (frozen contract); the concrete instance is the
 * `RbacService` superset created by `createRbacService`.
 */
export function registerModulePermissions(
  rbac: RBACService,
  defs: ReadonlyArray<PermissionDefinition>,
): void {
  (rbac as RbacService).registerPermissions(defs);
}

/** Late-update the user's memberships into the live RBAC (post sign-in). */
export function setRbacMemberships(
  rbac: RBACService,
  memberships: ReadonlyArray<Membership>,
): void {
  (rbac as RbacService).setMemberships(memberships);
}

/** Late-update the user's globalRole into the live RBAC (post sign-in). */
export function setRbacGlobalRole(rbac: RBACService, role: GlobalRole | undefined): void {
  (rbac as RbacService).setGlobalRole(role);
}
